import { HttpClient, HttpParams, HttpUrlEncodingCodec } from '@angular/common/http';
import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  getOrderUuid,
  getSelectedExhibitionId,
  getTicketLimitPerUserAccount,
  isUserLoggedIn,
  selectIsVoucherIncludedPerUserAccountLimit
} from '@app/app.reducer';
import { ExhibitorDetail } from '@core/data-access/models/exhibitor/exhibitor.model';
import { BaseRoute, StepRoute } from '@core/data-access/models/routes.model';
import { ProductBookingLoginGuard } from '@core/data-access/services/booking/product-booking-login-guard';
import { ExhibitorActions } from '@core/data-access/store/exhibitor/exhibitor.actions';
import { mapVoucherWelcomePreviewResponseToExhibitorDetail } from '@core/utils/data-mappers';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  BookingProductType,
  isBookingProductTypePackage,
  isBookingProductTypeTariff
} from '@products/models/booking.model';
import { PromoCode, PromoCodes } from '@products/models/smart-promotions';
import { Tariff } from '@products/models/tariff.model';
import {
  GetVoucherDetail,
  RedeemVoucher,
  RedeemVoucherResponse,
  ReleaseVoucher,
  SetVoucher,
  VoucherCode,
  VoucherDetail,
  VoucherDetailResponse,
  VoucherState
} from '@products/models/voucher.model';
import { VoucherValidationService } from '@products/services/voucher-validation.service';
import { VoucherWarningMessageService } from '@products/services/voucher-warning-message.service';
import { AppConstants } from '@shared/app-constants';
import { environment } from '@src/environments/environment';
import { getBookedProducts } from '@store/products/booking/product/product-booking.selectors';
import {
  getRemainingTicketCountForVoucherTariff,
  getTotalTicketCountPerVoucherTariffByTicketPersonId
} from '@store/products/booking/product/voucher/voucher-booking.selectors';
import { BookingSmartPromotionActions } from '@store/products/booking/smart-promotion/booking-smart-promotion.actions';
import { selectAllDiscountPromoCodes } from '@store/products/booking/smart-promotion/booking-smart-promotion.selectors';
import {
  getActiveVoucherTariffs,
  getInitialVoucherTariffByTicketPersonId,
  isRedeemedAnonymousVoucherProductInProductSelectionList
} from '@store/products/product-selection/product-selection.selectors';
import { SmartPromotionActions, VoucherProductActions } from '@store/products/product.actions';
import { StepName } from '@store/step-forms/step.interface';
import { SetSelectedStep } from '@store/step-forms/steps-forms.actions';
import { map as _map, cloneDeep, forEach, isEmpty, isEqual, isNil, join, some, trim } from 'lodash';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  filter,
  first,
  forkJoin,
  iif,
  map,
  of,
  pipe,
  switchMap,
  tap
} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class VoucherService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);
  private readonly voucherValidationService = inject(VoucherValidationService);
  private readonly voucherWarningMessageService = inject(VoucherWarningMessageService);
  private readonly productBookingLoginGuard = inject(ProductBookingLoginGuard);

  #voucherServiceState = signal<VoucherState>({
    voucherCode: null,
    isLoading: null,
    isProductSelection: null,
    isSelfRegistration: null,
    hasSponsorArea: null,
    isPromoCodeEnabled: null,
    checkPromoCodeDiscount: null,
    barcode: null,
    voucherTypingActive: null,
    isConfirmRedeemVoucherModalOpen: null
  });

  voucherServiceState = computed(this.#voucherServiceState);

  private readonly isRedeemVoucherCodeConfirmed$ = new BehaviorSubject(false);

  initState(voucherState: VoucherState) {
    this.#voucherServiceState.set(voucherState);
  }

  updateState(updatedVoucherState: Partial<VoucherState>) {
    this.#voucherServiceState.update((voucherServiceState) => ({ ...voucherServiceState, ...updatedVoucherState }));
  }

  setIsRedeemVoucherCodeConfirmed(isRedeemVoucherCodeConfirmed: boolean) {
    this.isRedeemVoucherCodeConfirmed$.next(isRedeemVoucherCodeConfirmed);
  }

  getIsRedeemVoucherCodeConfirmed$() {
    return this.isRedeemVoucherCodeConfirmed$;
  }

  redeemVoucher$(voucherRedeem: RedeemVoucher): Observable<RedeemVoucherResponse> {
    const httpUrlEncodingCodec = new HttpUrlEncodingCodec();
    const encodedPromoCode = httpUrlEncodingCodec.encodeValue(voucherRedeem.voucherCode);

    let params: HttpParams = new HttpParams();
    params = params.append('sr', `${this.voucherServiceState().isSelfRegistration}`);
    params = params.append('uuid', `${voucherRedeem.orderUuid}`);
    params = params.append('totalTicketCount', `${voucherRedeem.totalTicketCount}`);

    return this.http.get<RedeemVoucherResponse>(
      `${environment.protocol}${environment.webApiUrl}/voucher/redeem-voucher/${voucherRedeem.eventId}/${encodedPromoCode}`,
      { params }
    );
  }

  releaseVoucher$(releaseVoucher: ReleaseVoucher): Observable<void> {
    const httpUrlEncodingCodec = new HttpUrlEncodingCodec();
    const encodedPromoCode = httpUrlEncodingCodec.encodeValue(releaseVoucher.voucherCode);

    let body: HttpParams = new HttpParams();
    body = body.set('sr', `${this.voucherServiceState().isSelfRegistration}`);
    body = body.set('uuid', `${releaseVoucher.orderUuid}`);

    if (releaseVoucher.ticketPersonId) {
      body = body.set('ticketPersonId', `${releaseVoucher.ticketPersonId}`);
      body = body.set('remainingTicketCount', `${releaseVoucher.remainingTicketCount}`);
    }

    return this.http.post<void>(
      `${environment.protocol}${environment.webApiUrl}/voucher/reset-event-voucher/${releaseVoucher.eventId}/${encodedPromoCode}`,
      body
    );
  }

  releaseAllVouchers$() {
    return combineLatest([
      this.store.select(getSelectedExhibitionId),
      this.store.select(getOrderUuid),
      this.store.select(getActiveVoucherTariffs)
    ]).pipe(
      first(([eventId, orderUuid]) => !!eventId && !!orderUuid),
      switchMap(([eventId, orderUuid, activeVoucherTariffs]) => {
        const releaseVouchers = this.mapActiveVoucherTariffsToReleaseVoucher(activeVoucherTariffs, eventId, orderUuid);

        return this.releaseMultipleVouchers$(releaseVouchers);
      })
    );
  }

  scanVoucher($event: KeyboardEvent) {
    const code = $event.keyCode ? $event.keyCode : $event.which;

    this.voucherValidationService.updateState({
      isCurrentVoucherTypeAnonymous: false
    });

    this.voucherWarningMessageService.updateState({
      ticketLimitWarningMessage: '',
      voucherWarningMessage: '',
      invalidPromoCodeWarningMessage: '',
      invalidPromoCodes: ''
    });

    if (code === 16 || code === 17) {
      // don't add shift && ctrl to barcode!
      return;
    }

    const { barcode: voucherBarcode, voucherTypingActive } = this.voucherServiceState();

    if ($event.ctrlKey && code === 66) {
      this.updateState({ voucherTypingActive: true });
    } else if ($event.ctrlKey && code === 74) {
      this.updateState({
        voucherCode: voucherBarcode,
        barcode: '',
        voucherTypingActive: false
      });
      this.redeemVoucher(voucherBarcode);
    } else if (voucherTypingActive) {
      if ($event.shiftKey) {
        this.updateState({
          barcode: voucherBarcode + String.fromCharCode(code).toUpperCase()
        });
      } else {
        this.updateState({
          barcode: voucherBarcode + String.fromCharCode(code).toLowerCase()
        });
      }
    }
  }

  redeemVoucher(voucherCode: VoucherCode | PromoCode) {
    if (!this.voucherValidationService.isValidTariffLimit()) {
      return;
    }

    this.prepareAndValidateVoucherDetailRequestData$(trim(voucherCode))
      .pipe(
        tap(() => this.updateState({ isLoading: true })),
        switchMap((getVoucherDetail: GetVoucherDetail) =>
          this.getVoucherDetails$(getVoucherDetail).pipe(
            this.skipPromoCodeCheckOrCheckIfVoucherTariffBookingIsEnabledPipe(),
            this.openVoucherRedeemConfirmationModalPipe(),
            this.confirmOrContinueVoucherRedeemPipe(),
            this.setVoucherAndVoucherDetailPipe(getVoucherDetail.eventId)
          )
        ),
        first(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({
          voucherDetail: { eventId, isValidPromoCode, isCombinable, voucherCode, notCombinableWith },
          exhibitorDetail,
          setVoucher
        }) => {
          const { isProductSelection, isPromoCodeEnabled, checkPromoCodeDiscount, hasSponsorArea } =
            this.voucherServiceState();

          this.setIsRedeemVoucherCodeConfirmed(false);

          this.redeemAndReleaseVoucher(setVoucher, isProductSelection, eventId);

          this.setOrRedeemCombinablePromoCode(
            isValidPromoCode,
            isCombinable,
            isPromoCodeEnabled,
            checkPromoCodeDiscount,
            voucherCode,
            notCombinableWith
          );

          this.setExhibitorDetailAfterVoucherRedeem(exhibitorDetail, hasSponsorArea);

          this.updateState({ voucherCode: '', isLoading: false });
        },
        error: () => this.updateState({ isLoading: false }),
        complete: () => this.updateState({ isLoading: false })
      });
  }

  releaseMultipleVouchers$(releaseVouchers: ReleaseVoucher[]) {
    if (isEmpty(releaseVouchers)) {
      return of([]);
    }

    return forkJoin(
      releaseVouchers.map((releaseVoucher) => {
        return this.releaseVoucher$(releaseVoucher);
      })
    );
  }

  releaseVoucher(ticketPersonId: number, voucherCode: VoucherCode) {
    if (!ticketPersonId || !voucherCode) {
      return;
    }

    this.clearVoucherInputAndWarning();

    this.prepareReleaseVoucherPayloadData$(ticketPersonId, voucherCode).subscribe((releaseVoucher) =>
      this.store.dispatch(VoucherProductActions.releaseVoucher(releaseVoucher))
    );
  }

  releaseExpiredVoucher(ticketPersonId: number, voucherCode: VoucherCode) {
    if (!ticketPersonId || !voucherCode) {
      return;
    }

    this.clearVoucherInputAndWarning();

    this.prepareReleaseVoucherPayloadData$(ticketPersonId, voucherCode).subscribe((releaseVoucher) =>
      this.store.dispatch(VoucherProductActions.releaseExpiredVoucher(releaseVoucher))
    );
  }

  clearVoucherInputAndWarning() {
    this.updateState({
      voucherCode: ''
    });

    this.voucherWarningMessageService.updateState({
      ticketLimitWarningMessage: '',
      voucherWarningMessage: '',
      invalidPromoCodeWarningMessage: '',
      invalidPromoCodes: '',
      notCombinablePromoCodesWarningMessage: '',
      notCombinablePromoCodes: ''
    });
  }

  private prepareAndValidateVoucherDetailRequestData$(voucherCode: VoucherCode): Observable<GetVoucherDetail> {
    return combineLatest({
      eventId: this.store.select(getSelectedExhibitionId),
      allDiscountPromoCodes: this.store.select(selectAllDiscountPromoCodes)
    }).pipe(
      first(),
      filter(({ eventId }) => !isNil(eventId)),
      map(({ eventId, allDiscountPromoCodes }) => ({
        eventId,
        voucherCode,
        confirmedPromoCodes: _map(allDiscountPromoCodes, ({ promoCode }) => promoCode)
      }))
    );
  }

  private getVoucherDetails$(voucherDetail: GetVoucherDetail): Observable<VoucherDetailResponse> {
    const httpUrlEncodingCodec = new HttpUrlEncodingCodec();
    const encodedPromoCode = httpUrlEncodingCodec.encodeValue(voucherDetail.voucherCode);

    let params: HttpParams = new HttpParams();
    params = params.append('sr', `${this.voucherServiceState().isSelfRegistration}`);
    params = params.append('checkForPromoCode', `${this.voucherServiceState().isPromoCodeEnabled}`);

    forEach(voucherDetail.confirmedPromoCodes, (confirmedPromoCode) => {
      params = params.append('confirmedPromoCodes', confirmedPromoCode);
    });

    return this.http.get<VoucherDetailResponse>(
      `${environment.protocol}${environment.webApiUrl}/voucher/voucher-details/${voucherDetail.eventId}/${encodedPromoCode}`,
      { params }
    );
  }

  private skipPromoCodeCheckOrCheckIfVoucherTariffBookingIsEnabledPipe() {
    const checkIfVoucherTariffBookingIsEnabled$ = (voucherDetailResponse: VoucherDetailResponse) =>
      combineLatest({
        voucherTariff: this.store.select(getInitialVoucherTariffByTicketPersonId(voucherDetailResponse.ticketPersonId)),
        ticketLimitPerUserAccount: this.store.select(getTicketLimitPerUserAccount),
        isVoucherIncludedPerUserAccountLimit: this.store.select(selectIsVoucherIncludedPerUserAccountLimit)
      }).pipe(
        switchMap(({ voucherTariff, ticketLimitPerUserAccount, isVoucherIncludedPerUserAccountLimit }) =>
          iif(
            () => isNil(voucherTariff),
            of({ isValidVoucherTariff: false, isCurrentTariffBookingEnabled: false }),
            of({
              isValidVoucherTariff: true,
              isCurrentTariffBookingEnabled: this.productBookingLoginGuard.isCurrentTariffBookingEnabled(
                voucherTariff,
                ticketLimitPerUserAccount,
                isVoucherIncludedPerUserAccountLimit
              )
            })
          )
        ),
        switchMap(({ isValidVoucherTariff, isCurrentTariffBookingEnabled }) =>
          iif(
            () => !isValidVoucherTariff || isCurrentTariffBookingEnabled,
            of(voucherDetailResponse),
            of(voucherDetailResponse).pipe(
              tap(() => {
                this.productBookingLoginGuard.showLogin();

                this.updateState({ isLoading: false });
              }),
              waitForUserToLogInWhenVoucherTariffBookingIsDisabledPipe
            )
          )
        )
      );

    const waitForUserToLogInWhenVoucherTariffBookingIsDisabledPipe = pipe<
      Observable<VoucherDetailResponse>,
      Observable<VoucherDetailResponse>
    >(
      switchMap((voucherDetailResponse) =>
        this.store.pipe(
          select(isUserLoggedIn),
          first((isUserLoggedIn) => isUserLoggedIn),
          map(() => voucherDetailResponse)
        )
      )
    );

    return pipe<Observable<VoucherDetailResponse>, Observable<VoucherDetailResponse>>(
      switchMap((voucherDetailResponse) =>
        iif(
          () => voucherDetailResponse.isValidPromoCode,
          of(voucherDetailResponse),
          checkIfVoucherTariffBookingIsEnabled$(voucherDetailResponse)
        )
      )
    );
  }

  private openVoucherRedeemConfirmationModalPipe() {
    return pipe(
      tap(({ isValidVoucher }: VoucherDetailResponse) => {
        const isVoucherNotRedeemedOnProductSelection = isValidVoucher && !this.voucherServiceState().isProductSelection;

        this.updateState({
          isConfirmRedeemVoucherModalOpen: isVoucherNotRedeemedOnProductSelection
        });
      })
    );
  }

  private confirmOrContinueVoucherRedeemPipe() {
    const filterAcceptedVoucherCodeRedeemPipe = pipe<
      Observable<VoucherDetailResponse>,
      Observable<VoucherDetailResponse>
    >(
      filter(({ voucherCode }) => {
        const inputRedeemedVoucherCode = this.voucherServiceState().voucherCode;
        const isUrlParamVoucherRedeem = !isEmpty(voucherCode) && isEmpty(inputRedeemedVoucherCode);
        const isCurrentConfirmedRedeemedVoucherCode = isEqual(voucherCode, inputRedeemedVoucherCode);

        return isUrlParamVoucherRedeem || isCurrentConfirmedRedeemedVoucherCode;
      })
    );

    return pipe(
      switchMap((voucherDetailResponse: VoucherDetailResponse) =>
        iif(
          () => this.voucherServiceState().isConfirmRedeemVoucherModalOpen,
          this.isRedeemVoucherCodeConfirmed$.pipe(
            tap(() => this.updateState({ isLoading: false })),
            filter((isRedeemVoucherCodeConfirmed) => isRedeemVoucherCodeConfirmed),
            tap(() => this.updateState({ isConfirmRedeemVoucherModalOpen: false })),
            map(() => voucherDetailResponse)
          ),
          of(voucherDetailResponse)
        )
      ),
      filterAcceptedVoucherCodeRedeemPipe
    );
  }

  private setVoucherAndVoucherDetailPipe(eventId: number) {
    return pipe(
      switchMap((voucherDetailResponse: VoucherDetailResponse) => {
        const voucherDetail = this.mapVoucherDetailResponseToVoucherDetail(voucherDetailResponse, eventId);

        const exhibitorDetail = mapVoucherWelcomePreviewResponseToExhibitorDetail(
          voucherDetailResponse.voucherWelcomePreview
        );

        return this.voucherValidationService
          .isValidVoucherDetail$(voucherDetail)
          .pipe(
            switchMap((isValidVoucherDetail) =>
              iif(
                () => isValidVoucherDetail,
                this.prepareRedeemVoucherPayloadData$(voucherDetail, exhibitorDetail),
                of({ voucherDetail, exhibitorDetail, setVoucher: null })
              )
            )
          );
      })
    );
  }

  private prepareRedeemVoucherPayloadData$(
    voucherDetail: VoucherDetail,
    exhibitorDetail: ExhibitorDetail
  ): Observable<{
    voucherDetail: VoucherDetail;
    exhibitorDetail: ExhibitorDetail;
    setVoucher: SetVoucher;
  }> {
    const { eventId, ticketPersonId, voucherCode } = voucherDetail;

    return combineLatest({
      orderUuid: this.store.select(getOrderUuid),
      bookedProducts: this.store.select(getBookedProducts),
      activeVoucherTariffs: this.store.select(getActiveVoucherTariffs),
      isRedeemedAnonymousVoucherProductInProductSelectionList: this.store.select(
        isRedeemedAnonymousVoucherProductInProductSelectionList
      ),
      totalTicketCount: this.store.select(getTotalTicketCountPerVoucherTariffByTicketPersonId(ticketPersonId)),
      remainingTicketCount: this.store.select(getRemainingTicketCountForVoucherTariff(ticketPersonId, voucherCode))
    }).pipe(
      first(({ orderUuid }) => !isNil(orderUuid)),
      map(
        ({
          orderUuid,
          bookedProducts,
          activeVoucherTariffs,
          isRedeemedAnonymousVoucherProductInProductSelectionList,
          totalTicketCount,
          remainingTicketCount
        }) => {
          const countryCode = this.translateService.currentLang;
          const isAnonymous = this.voucherValidationService.voucherValidationState().isCurrentVoucherTypeAnonymous;
          const setVoucher: SetVoucher = {
            redeemVoucher: {
              eventId,
              orderUuid,
              voucherCode,
              countryCode,
              isAnonymous,
              totalTicketCount: totalTicketCount + AppConstants.VOUCHER_PRODUCT_DEFAULT_AMOUNT
            },
            releaseVouchers: [],
            removeProductBookings: []
          };

          if (
            this.voucherServiceState().isSelfRegistration ||
            (isAnonymous && !isRedeemedAnonymousVoucherProductInProductSelectionList)
          ) {
            forEach(activeVoucherTariffs, ({ ticketPersonId, voucherCode }) => {
              const releaseVoucher: ReleaseVoucher = {
                eventId,
                orderUuid,
                ticketPersonId,
                voucherCode,
                countryCode,
                remainingTicketCount
              };

              setVoucher.releaseVouchers.push(releaseVoucher);
            });

            const cloneBookedProducts: BookingProductType[] = cloneDeep(bookedProducts);

            forEach(cloneBookedProducts, (bookedProduct: BookingProductType) => {
              if (isBookingProductTypeTariff(bookedProduct)) {
                forEach(bookedProduct.tariffs, (bookedTariff) => {
                  bookedTariff.count = 0;
                  bookedTariff.tariffUuids = [];
                });

                const isBookingTariffTypeInRemoveProductBookings = some(
                  setVoucher.removeProductBookings,
                  (removeProductBooking) => {
                    if (isBookingProductTypeTariff(removeProductBooking)) {
                      return removeProductBooking.ticketTypeId === bookedProduct.ticketTypeId;
                    }
                  }
                );

                if (!isBookingTariffTypeInRemoveProductBookings) {
                  setVoucher.removeProductBookings.push(bookedProduct);
                }
              } else if (isBookingProductTypePackage(bookedProduct)) {
                bookedProduct.productTypes.forEach((bookedPackageProduct) => {
                  if (isBookingProductTypeTariff(bookedPackageProduct)) {
                    forEach(bookedPackageProduct.tariffs, (bookedPackageTariff) => {
                      bookedPackageTariff.count = 0;
                      bookedPackageTariff.tariffUuids = [];
                    });
                  }
                });

                setVoucher.removeProductBookings.push(bookedProduct);
              }
            });
          }

          return { voucherDetail, exhibitorDetail, setVoucher };
        }
      )
    );
  }

  private prepareReleaseVoucherPayloadData$(
    ticketPersonId: number,
    voucherCode: VoucherCode
  ): Observable<ReleaseVoucher> {
    return combineLatest({
      eventId: this.store.select(getSelectedExhibitionId),
      orderUuid: this.store.select(getOrderUuid),
      remainingTicketCount: this.store.select(getRemainingTicketCountForVoucherTariff(ticketPersonId, voucherCode))
    }).pipe(
      first(({ eventId, orderUuid }) => !isNil(eventId) && !isNil(orderUuid)),
      map(({ eventId, orderUuid, remainingTicketCount }) => ({
        eventId,
        orderUuid,
        ticketPersonId,
        voucherCode,
        countryCode: this.translateService.currentLang,
        remainingTicketCount
      }))
    );
  }

  private mapVoucherDetailResponseToVoucherDetail(
    voucherDetailResponse: VoucherDetailResponse,
    eventId: number
  ): VoucherDetail {
    const {
      groupId,
      ticketTypeId,
      ticketPersonId,
      voucherCode,
      voucherType,
      isValidVoucher,
      isValidPromoCode,
      isCombinable,
      notCombinableWith
    } = voucherDetailResponse;

    return {
      eventId,
      ticketTypeId: groupId,
      ticketPersonTypeId: ticketTypeId,
      ticketPersonId,
      voucherCode,
      voucherType,
      isValidVoucher,
      isValidPromoCode,
      isCombinable,
      notCombinableWith
    };
  }

  private mapActiveVoucherTariffsToReleaseVoucher(
    activeVoucherTariff: Tariff[],
    eventId: number,
    orderUuid: string
  ): ReleaseVoucher[] {
    const releaseVouchers: ReleaseVoucher[] = [];
    const countryCode = this.translateService.currentLang;

    forEach(activeVoucherTariff, ({ ticketPersonId, voucherCode }) => {
      const releaseVoucher: ReleaseVoucher = {
        eventId,
        orderUuid,
        ticketPersonId,
        voucherCode,
        countryCode,
        remainingTicketCount: 0
      };

      releaseVouchers.push(releaseVoucher);
    });

    return releaseVouchers;
  }

  private redeemAndReleaseVoucher(setVoucher: SetVoucher, isProductSelection: boolean, eventId: number) {
    if (setVoucher) {
      this.store.dispatch(VoucherProductActions.redeemAndReleaseVoucher(setVoucher));

      this.navigateToProductSelectionStep(isProductSelection, eventId);
    }
  }

  private navigateToProductSelectionStep(isProductSelection: boolean, eventId: number) {
    if (!isProductSelection) {
      this.store.dispatch(new SetSelectedStep(StepName.Tickets));

      this.router.navigate([`/${BaseRoute.WebShop}/${eventId}/${StepRoute.Tickets}`]);
    }
  }

  private setOrRedeemCombinablePromoCode(
    isValidPromoCode: boolean,
    isCombinable: boolean,
    isPromoCodeEnabled: boolean,
    checkPromoCodeDiscount: boolean,
    promoCode: PromoCode,
    notCombinableWith: PromoCodes
  ) {
    if (!isCombinable) {
      const notCombinablePromoCodes = join(notCombinableWith, ', ');

      this.voucherWarningMessageService.setNotCombinablePromoCodeWarningMessage(notCombinablePromoCodes);

      return;
    }

    if (isValidPromoCode && isPromoCodeEnabled) {
      const promoCodeAction = checkPromoCodeDiscount
        ? SmartPromotionActions.redeemPromoCode(promoCode)
        : BookingSmartPromotionActions.setUnredeemedPromoCode({ promoCode, isRedeemedAndApplied: false });

      this.store.dispatch(promoCodeAction);
    }
  }

  private setExhibitorDetailAfterVoucherRedeem(exhibitorDetail: ExhibitorDetail, hasSponsorArea: boolean) {
    this.store.dispatch(ExhibitorActions.setExhibitorDetailAfterVoucherRedeem({ exhibitorDetail, hasSponsorArea }));
  }
}
